/**
 * HVILKEN MODUL HVER RESSURS HØRER TIL (G1, QA13 #1 og #2; Atle 253.6, 9. oktober).
 *
 * Før dette fantes tre svar på «hva har denne organisasjonen?»: modulene fra Control Room, den
 * eldre typen (`organizations.kind`) og særregler som «byrå med solgte jobber» (F24). Menyen brukte
 * alle tre, API-et sjekket modulen bare når noe ble OPPRETTET (seks ruter), og en festival som slo
 * av Konserter kunne fortsatt lese og endre konsertene gjennom API-et.
 *
 * Nå er det én tabell, og `can()` leser den: en handling på en ressurs som hører til en modul
 * organisasjonen ikke har, får nei med grunnen `module_disabled` — lesing som skriving. Menyen og
 * rutene spør `can()`, og API-et spør den samme gjennom `PolicyGuard`, så alle tre følger tabellen.
 *
 * TO SLAG VERDIER:
 *   `CORE`        felles for alle (kontakter, oppgaver, innboks, kalender, dokumenter, innstillinger,
 *                 deling, økonomi). Ingen modul trengs.
 *   [moduler]     ressursen hører til MINST ÉN av dem. Flere står der når to moduler deler den samme
 *                 ressursen: en sal (`venue`) er både spillestedets egen og den andre leier,
 *                 og en programpost står i festivalens program, på spillestedets arrangement og på en
 *                 konsert. Én av modulene holder.
 *
 * `Record<ResourceType, …>` med vilje: en ny ressurs uten en linje her er en typefeil, ikke en
 * ressurs som stille blir felles for alle.
 *
 * Den GAMLE REGELEN «å slå av en modul skjuler, sletter ingenting» står: radene blir liggende og
 * kommer tilbake når modulen slås på igjen.
 */
import type { OrganizationModule } from '../contracts/control-room-modules.js';
import type { Action, Actor, DenyReason, Resource, ResourceType } from './types.js';

export const CORE = 'core' as const;
export type ResourceModules = typeof CORE | readonly OrganizationModule[];

/*
 * Arrangementene: festivalen og spillestedet er begge en `festivals`-rad med årganger, og konsertene
 * ligger i en årgang i en beholder ingen ser (0407, `festival_kind = 'concerts'`). Booking
 * programmerer i en årgang (analyse 25), så den leser dem også. Menyen skiller Festivaler fra
 * Spillested med modulen som kontekst (`inModule` på ressursen), ikke med en egen ressurs.
 */
const EVENTS: readonly OrganizationModule[] = ['festival', 'venue', 'concerts', 'booking'];
/*
 * Produksjonen: mannskap, vakter, akkreditering, frivillige, scener og kartet. Festivalen og
 * spillestedet (231.5) — og arrangørens konserter, som har vakter, kart og folk på samme måte
 * (`/shifts`, kartet og Personer brukes fra konsertsiden). Bredt med vilje: å stenge en konsert
 * ute av sine egne vakter ville vært en regresjon, og Rettigheter-seksjonene har sin egen regel.
 */
const PRODUCTION: readonly OrganizationModule[] = ['festival', 'venue', 'concerts'];
/* Det som står på en programpost eller en konsert: ressursene, rommene og deltakelsen. */
const STAGED: readonly OrganizationModule[] = ['festival', 'venue', 'concerts'];

export const RESOURCE_MODULES: Readonly<Record<ResourceType, ResourceModules>> = {
  organization: CORE,
  festival: EVENTS,
  edition: EVENTS,
  person: CORE,
  contact: CORE,
  task: CORE,
  thread: CORE,
  message: CORE,
  department: CORE,
  document: CORE,
  /* Programposten: festivalens program, spillestedets arrangement og arrangørens konsert. */
  performance: STAGED,
  economy: CORE,
  stage: PRODUCTION,
  accreditation_level: PRODUCTION,
  crew_member: PRODUCTION,
  shift: PRODUCTION,
  event: CORE,
  volunteer: PRODUCTION,
  signup_form: PRODUCTION,
  artist_agreement: ['agency'],
  artist: ['agency'],
  broadcast: PRODUCTION,
  accreditation: PRODUCTION,
  folder: CORE,
  contract: CORE,
  resource_type: STAGED,
  resource: STAGED,
  allocation: STAGED,
  person_field: CORE,
  participation_role: STAGED,
  participation: STAGED,
  membership: CORE,
  member_profile: CORE,
  superadmin_profile: CORE,
  organization_partner: CORE,
  invitation: CORE,
  tag: CORE,
  notification: CORE,
  draft: CORE,
  object_grant: CORE,
  audit_log: CORE,
  user: CORE,
  consent: CORE,
  role_permission: CORE,
  /* En sal: spillestedets egen (Spillested) og den arrangøren leier (Spillesteder). */
  venue: ['venue', 'venues'],
  venue_rate: ['venue'],
  venue_enquiry: ['venue'],
  /* Arrangementet på spillestedet; konsertens produksjonsadvance og den som leier ser det også. */
  venue_night: ['venue', 'venues', 'concerts'],
  venue_registry: ['venues'],
  /* Datoforespørselen sendes fra markedsplassen eller fra en konsert. */
  date_request: ['venues', 'concerts'],
  concert: ['concerts'],
  concert_budget: ['concerts'],
  concert_template: ['concerts'],
  agency_move: ['booking'],
  /* Varselet om bud uten ansvarlig gjelder begge sider av bordet (F21). */
  booking_enquiry: ['booking', 'agency'],
  export: CORE,
  external: CORE,
  share_link: CORE,
  share: CORE,
  shared_advance: CORE,
  map: PRODUCTION,
  booking: ['booking'],
  booking_artist: ['booking'],
  booking_draft: ['booking'],
  /* Budet gis fra registeret (Booking) eller fra en konsert (Konserter, QA G-9). */
  booking_bid: ['booking', 'concerts'],
  booking_amount: ['booking', 'concerts'],
  agency_booking: ['agency'],
};

export function modulesOfResource(resource: ResourceType): ResourceModules {
  return RESOURCE_MODULES[resource];
}

/** Modulene en handling hører til — ressursen før kolonet. */
export function modulesOfAction(action: Action): ResourceModules {
  return modulesOfResource(action.slice(0, action.indexOf(':')) as ResourceType);
}

/** Har aktørens organisasjon en av modulene handlingen hører til? Uten modulene på aktøren: ja. */
export function hasModuleFor(actor: Pick<Actor, 'modules'>, action: Action): boolean {
  const modules = modulesOfAction(action);
  if (modules === CORE || actor.modules === undefined) return true;
  return modules.some((module) => actor.modules?.has(module) === true);
}

/**
 * STEGET I `can()`. Bare for en rad i aktørens EGET hus: en delt advance hører til huset som eier
 * den, og det er det husets modul som gjelder (arrangøren har Festival, byrået som fører advancen
 * med, har det ikke).
 *
 * UTEN MODULENE PÅ AKTØREN slipper steget gjennom. Det gjelder gjesten (`resolve_guest_actor`
 * bærer dem ikke — gjesten har ingen moduler, bare delinger) og kalenderstrømmen, som bygger
 * aktøren uten `enter_organization`. Rutene i API-et får alltid aktøren fra `enter_organization`,
 * som bærer dem (0526).
 */
export function moduleGate(actor: Actor, action: Action, resource: Resource): true | DenyReason {
  if (resource.organizationId !== actor.organizationId) return true;
  /*
   * SPURT INNE I EN BESTEMT MODUL (`inModule`): menypunktet Spillesteder og seksjonen Spillested
   * spør begge `venue:list`, men om hver sin modul. Konteksten kan bare SMALNE inn — den må stå i
   * tabellen for handlingen (eller handlingen er felles), og organisasjonen må ha den.
   */
  const context = resource.inModule;
  if (context !== undefined) {
    const modules = modulesOfAction(action);
    if (modules !== CORE && !modules.includes(context)) return 'module_disabled';
    if (actor.modules !== undefined && !actor.modules.has(context)) return 'module_disabled';
  }
  return hasModuleFor(actor, action) ? true : 'module_disabled';
}
